// @flow
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import coverImg from '../../../assets/img/meta-cover-lightblue.png';
import environment from '../../../environment';
import { defineMessages, intlShape } from 'react-intl';

import style from './TermsService.scss';

import type { Context } from 'koa';
import type { LocaleState } from '../../locale/duck';

type Props = {
  +locale: LocaleState;
};

const messages = defineMessages({
  US: {
    title: {
      id: 'terms.service.title.US',
    },
    description: {
      id: 'terms.service.description.US',
    },
    titleOg: {
      id: 'terms.service.title.og.US',
    },
    descriptionOg: {
      id: 'terms.service.description.og.US',
    },
  },
  PE: {
    title: {
      id: 'terms.service.title.PE',
    },
    description: {
      id: 'terms.service.description.PE',
    },
    titleOg: {
      id: 'terms.service.title.og.PE',
    },
    descriptionOg: {
      id: 'terms.service.description.og.PE',
    },
  },
  CO: {
    title: {
      id: 'terms.service.title.CO',
    },
    description: {
      id: 'terms.service.description.CO',
    },
    titleOg: {
      id: 'terms.service.title.og.CO',
    },
    descriptionOg: {
      id: 'terms.service.description.og.CO',
    },
  },
  default: {
    title: {
      id: 'terms.service.title',
    },
    description: {
      id: 'terms.service.description',
    },
    titleOg: {
      id: 'terms.service.title.og',
    },
    descriptionOg: {
      id: 'terms.service.description.og',
    },
  },
});

const tsES = <section className={style.termsServiceWrap}>
  <h1>TÉRMINOS Y CONDICIONES DE USO DEL PORTAL QEMPO</h1>
  <p>Los presentes Términos y Condiciones (en adelante <strong>“<u><i>Términos y Condiciones</i></u>”</strong>) regulan
    su registro, acceso y/o uso de los contenidos y/o servicios dispuestos en el portal de Internet denominado Qempo,
    ubicado en el Identificador Uniforme de Recursos (en adelante, el “URL”): <a href="https://www.qempo.com"
                                                                                 target="_blank">
      https://www.qempo.com</a>; <a href="https://www.qempo.com.pe" target="_blank">https://www.qempo.com.pe
    </a> y <a href="https://www.qempo.com.pe" target="_blank">https://www.qempo.pe</a> (en adelante,
    el <strong>“<u><i>Portal</i></u>”</strong>)
    que Qempo, LLC empresa americana legalmente constituida en Estados Unidos (en adelante,
    <strong>“<u><i>Qempo</i></u>”</strong>) pone a disposición de sus usuarios.</p>
  <br/>
  <p>Su registro, acceso y/o uso del contenido y/o de los servicios provistos por medio del Portal atribuyen a usted la
    condición de usuario del Portal (en adelante, “Usuario”, y encontrándose comprendido en las
    expresiones singular <strong>“Usuario”</strong> y <strong>“Usted”</strong>, y en la
    plural <strong>“Usuarios”</strong>) e implica que usted acepta, plenamente y sin reservas, todas y cada una de las
    disposiciones contenidas en estos Términos y Condiciones, en la versión publicada por Qempo en el momento mismo en
    que Usted acceda al Portal para registrarse, acceder y/o usar cualquiera de sus contenidos y/o servicios. En
    consecuencia, usted declara reconocer y aceptar que se encuentra obligado a leer completamente los presentes
    Términos y Condiciones en cada una de las ocasiones en que se proponga acceder y/o utilizar los contenidos y/o
    servicios dispuestos en el Portal, ya que pueden ser objeto de modificaciones a sola discreción de Qempo, y sin
    necesidad de notificación previa ni adicional a la sola publicación en el Portal.</p>
  <br/>
  <p><strong>SI USTED NO ESTÁ DE ACUERDO CON LOS PRESENTES TÉRMINOS Y CONDICIONES, LOS CUALES TIENEN UN CARÁCTER
    OBLIGATORIO Y VINCULANTE PARA USTED, DEBERÁ DETENER SU PROCESO DE REGISTRO EN EL PORTAL O, DE YA HABERLO REALIZADO,
    ABSTENERSE DE ACCEDER O UTILIZAR EL MISMO, SUS CONTENIDOS Y/O SERVICIOS.</strong></p>
  <h2>1. SOBRE QEMPO Y EL PORTAL</h2>
  <p>Mediante el Portal, Qempo facilita la interacción de personas que desean adquirir productos comercializados en
    territorios distintos a las ciudades en las cuales se encuentran (en adelante, el <strong>“Comprador”</strong> o
    los <strong>“Compradores”</strong>), con diversos proveedores logísticos, que podrán ser viajeros o empresas de
    carga internacional (en adelante, los <strong>“Proveedores Logísticos”</strong>), que se encargan de comprar y/o
    recibir los productos, gestionar el envío y/o importación de los mismos, así como realizar la entrega a los
    Compradores, a cambio de una contraprestación económica (en adelante, la <strong>“Contraprestación”</strong>)
    comunicada previamente al Comprador (en adelante, los “Servicios”).</p>
  <br/>
  <h2>2. CONDICIONES DE REGISTRO, ACCESO Y USO DEL PORTAL</h2>
  <h3>2.1 Capacidad Legal</h3>
  <p>El uso del Portal está disponible para personas que tengan capacidad legal para contratar. Para el registro, acceso
    y uso del Portal, el Usuario declara tener, como mínimo, dieciocho (18) años de edad.</p>
  <h3>2.2 Registro del Usuario e Inicio de Sesión</h3>
  <p>El Usuario, al registrarse para usar el Portal, deberá proporcionar a Qempo la información requerida en el
    formulario correspondiente, y deberá elegir una contraseña para el acceso a su cuenta. Una vez enviados los datos
    indicados, el Usuario será direccionado hacia su cuenta dentro del Portal.</p>
  <br/>
  <p>El Usuario se obliga a proveer información verdadera, correcta, actual y completa, conforme lo requerido en el
    formulario de registro en el Portal (los <strong>“Datos de Registro”</strong>). Asimismo, se obliga a mantener y
    actualizar en todo momento los Datos de Registro a fin de conservarlos veraces, correctos, actuales y completos. En
    todo caso, el Usuario será el único responsable de las manifestaciones falsas o inexactas que realice y de los
    perjuicios que cause a Qempo, los Proveedores Logísticos, o a terceros. Si el Usuario suministra información que es
    falsa, inexacta, desactualizada o incompleta, o si Qempo tiene bases razonables para sospechar que dicha información
    es falsa, inexacta, desactualizada o incompleta, Qempo tendrá el derecho de suspender o cerrar su cuenta y negarle
    el uso presente o futuro de los Servicios (o cualquier parte del mismo).</p>
  <br/>
  <p>El Usuario debe asegurarse de que la dirección de correo electrónico que proporcione sea válida y que continúe
    siéndolo durante la vigencia del presente acuerdo, ya que toda comunicación de Qempo al Usuario fuera del Portal
    será remitida a la dirección de correo electrónico empleada para su acceso al Portal y empleada para contratar los
    Servicios. El Usuario se obliga utilizar correos electrónicos propios, ya sea para el acceso al Portal como para la
    contratación de los Servicios.</p>
  <br/>
  <p>El Usuario podrá establecer que su identificación se realice mediante una conexión a su cuenta de Facebook, de modo
    que se obliga a brindar información correcta y completa mediante su registro en el Portal vinculando su perfil de la
    red social ubicada en la URL <a href="https://www.facebook.com" target="_blank">https://www.facebook.com</a> (en
    adelante, <strong>“Facebook”</strong>) a su cuenta de Qempo.</p>
  <br/>
  <p>De acuerdo a lo establecido en la Ley N° 29733, Ley de Protección de Datos Personales, se informa al Usuario que
    los datos personales que se obtengan como consecuencia de su registro serán incorporados a un banco de datos de
    titularidad de Qempo (en adelante, el <strong>“Banco de Datos”</strong>). El Banco de Datos será tratado según las
    condiciones descritas en la sección de Protección de Datos Personales de los Términos y Condiciones.</p>
  <br/>
  <p>El Usuario es responsable de mantener la confidencialidad de sus datos de inicio de sesión y contraseña, así como
    de prevenir intrusiones a los sistemas de cómputo y/o dispositivos móviles usados para acceder al Portal y/o a los
    Servicios. En tal sentido, el Usuario será considerado el único responsable de las acciones y actividades que se
    realicen bajo sus datos de inicio de sesión y/o con la contraseña utilizada para acceder y/o utilizar el Portal. El
    Usuario se obliga a no revelar, directa ni indirectamente, su contraseña a terceros por ninguna razón y/o
    circunstancia, así como también a no permitir que terceros utilicen sus datos de inicio de sesión y/o
    contraseña.</p>
  <br/>
  <p>En caso que el Usuario pierda su nombre de usuario y/o su contraseña, o si cree que dicha información ha sido
    comprometida de cualquier manera, y/o si sospecha que su cuenta ha sido visitada por terceros no autorizados, el
    Usuario se obliga a cambiar su contraseña inmediatamente y a notificar a Qempo, en el más breve plazo que le sea
    posible, enviando un mensaje a la dirección <a href="mailto:seguridad@qempo.com">seguridad@qempo.com</a>.</p>
  <h3>2.3 Veracidad de la Información</h3>
  <p>Toda la información que brinde el Usuario a Qempo deberá ser veraz. Para estos efectos, el Usuario garantiza la
    autenticidad de todos aquellos datos que envíe a Qempo durante su registro y uso del Portal. </p>
  <br/>
  <p>Qempo no controla, monitorea, revisa ni valida la información suministrada por los Usuarios durante su proceso de
    su registro en el Portal, ni durante su permanencia en el mismo, por lo que presume de buena fe que toda la
    información proporcionada por los Usuarios es correcta, completa, lícita y adecuada a los presentes Términos y
    Condiciones, a la ley, a la moral y a las buenas costumbres.</p>
  <br/>
  <p>El Usuario declara que Qempo no será responsable por cualquier daño y/o perjuicio que pudiera sufrir el Usuario,
    y/o cualquier otra persona, como consecuencia de falsedad, errores, defectos y/u omisiones en la información
    facilitada por cualquiera de los Usuarios en el Portal y/o por el uso del Portal. </p>
  <br/>
  <p>Asimismo, Qempo se reserva el derecho a suspender y/o eliminar la cuenta del Usuario en el Portal, así como también
    a informar a las autoridades gubernamentales correspondientes en caso de detectar la inclusión de datos o
    información falsos e/o inexactos, así como también ante cualquier supuesto caso de suplantantación de identidad en
    el Portal.</p>
  <h3>2.4 Obligación de Hacer un Uso Correcto del Portal</h3>
  <p>Cada Usuario podrá ser titular sólo de una (01) cuenta en el Portal, quedando prohibido crear más cuentas con
    distintas direcciones de correo electrónico, o falseando, modificando o alterando sus datos personales de cualquier
    manera. Si Qempo detectara que un Usuario posee más de una (01) cuenta, Qempo podrá, a su sola discreción, (i)
    mantener vigente la primera cuenta que el Usuario haya creado y anular todas las demás, sin necesidad de
    comunicación previa al Usuario para tales efectos; y/o (ii) dar de baja todas las cuentas del Usuario incluyendo, en
    ambos casos, la anulación de cualquier pedido, orden de compra y/o solicitud en general, a su sola discreción, sin
    que el Usuario tenga derecho a pago ni reembolso alguno, y por ningún concepto.</p>
  <br/>
  <p>El Usuario se obliga a acceder al Portal y a utilizar el Servicio de buena fe, conforme a ley, y de acuerdo con los
    presentes Términos y Condiciones. En tal sentido, el Usuario:</p>
  <ol type="i">
    <li>No deberá compartir, comercializar, intercambiar, vender, comprar ni distribuir datos de inicio de sesión y/o
      contraseñas para acceder y/o utilizar el Portal y/o al Servicio.
    </li>
    <li>No deberá usar ningún lenguaje, que a solo criterio de Qempo pueda ser considerado ilícito, amenazante, abusivo,
      obsceno, pernicioso, difamatorio, vulgar, acosador, aborrecible, racista y/o censurable, ni cualquier lenguaje que
      pueda ser considerado inapropiado y/o en contra de la moral y las buenas costumbres durante sus comunicaciones
      dentro del Portal, con el personal de Qempo, con cualquier Viajero y/o con cualquier Comprador.
    </li>
    <li>No deberá usar el Portal y/o el Servicio ofrecidos por Qempo para propósitos inadecuados e/o ilícitos.</li>
    <li>Se encuentra prohibido realizar transacciones con los Proveedores Logísticos por medios distintos al Portal. Sin
      perjuicio de ello, el Usuario declara y reconoce que Qempo no asume responsabilidad alguna por las transacciones
      que se puedan llevar a cabo entre el Usuario y los Proveedores Logísticos por medios distintos del Portal.
    </li>
    <li>No deberá proporcionar enlaces a ningún sitio que contenga material indebido y/o ilícito y/o que pueda dañar y/o
      deteriorar los sistemas informáticos de Qempo.
    </li>
    <li>No deberá sacar ventaja indebida de ningún problema y/o falla en la función, diseño u operatividad del Portal
      y/o del Servicio. No deberá informar, directa ni indirectamente, la existencia de dicho problema y/o falla a
      terceros.
    </li>
    <li>No deberá comunicar ningún dato personal, información privada, sensible ni íntima de cualquier otro Usuario y/o
      de terceras personas por medio del Portal, de ninguna manera y bajo ninguna circunstancia.
    </li>
    <li>No deberá utilizar, crear ni brindar alguna simulación y/o copia fraudulenta del sitio del Portal y/o de los
      servidores de Qempo y/o de sus personas vinculadas.
    </li>
    <li>No deberá intentar interferir, realizar ingeniería inversa y/o descifrar ninguna transmisión de los servidores
      de Qempo y/o de sus personas vinculadas y/o del Portal.
    </li>
    <li>No deberá infringir los derechos de propiedad industrial y/o intelectual de Qempo, de sus personas vinculadas,
      ni de terceros. El Usuario no deberá cargar, trasmitir y/o comunicar en el Portal, ni a través del Portal,
      materiales protegidos por derechos de propiedad intelectual, salvo que el Usuario tenga la autorización necesaria
      y suficiente para realizar tales acciones por parte de los respectivos titulares de derechos de propiedad
      intelectual.
    </li>
    <li>No deberá interferir con la capacidad y libertad de otros Usuarios para acceder y/o usar el Portal y/o el
      Servicio.
    </li>
  </ol>
  <p>El incumplimiento por parte del Usuario de cualquiera de las condiciones precedentemente listadas, u otras
    contenidas en los presentes Términos y Condiciones, otorgará a Qempo la posibilidad de suspender y/o dar de baja de
    manera definitiva a la cuenta del Usuario infractor y, de considerarlo pertinente, Qempo podrá denunciar los hechos
    en cuestión ante las autoridades correspondientes.</p>
  <h3>2.5 Suspensión y Baja Definitiva de Usuarios </h3>
  <p>Qempo tiene la capacidad de emitir advertencias, suspensiones temporales y bajas definitivas a Usuarios en
    cualquier momento, sin expresión de causa ni justificación alguna, lo cual no se encontrará condicionado únicamente
    a aquellos Usuarios que infrinjan los presentes Términos y Condiciones y/o cualquier aviso, reglamento de uso e/o
    instrucción puesta en conocimiento por Qempo a los Usuarios.</p>
  <p>En ese sentido, Qempo se reserva el criterio con respecto a cuándo, a quién y a cómo imponer advertencias,
    sanciones y/o acciones disciplinarias. La severidad de un incumplimiento y el número de ocurrencias se toman en
    cuenta en las decisiones, sin perjuicio de las acciones que Qempo decida implementar a su sola discreción.</p>
  <h3>2.6 Solicitud de eliminación de datos</h3>
  <p>Todo usuario que desee solicitar la eliminación de sus datos de la “Base de datos” de Qempo, debe hacerlo 
    formalmente a través de una solicitud a hola@qempo.com solicitando la eliminación de todos sus datos. Qempo 
    verificará la solicitud y de cumplir todas las normas, procederá a eliminar sus datos sin ninguna objeción.</p>
  <br/>
  <h2>3. PRODUCTOS, PEDIDOS, COMPRADORES Y VIAJEROS</h2>
  <h3>3.1 Sobre el Comprador y el Viajero</h3>
  <p>El Usuario será considerado Comprador cuando, a través del Portal, publique su intención de compra, incluyendo el
    URL del producto específico (en adelante, el <strong>“Producto”</strong>) que desee adquirir en una determinada
    tienda online (en adelante, la <strong>“Tienda”</strong>), con la finalidad de que un Proveedor Logístico lo compre,
    reciba o recoja, transporte y entregue o envíe al Comprador. Qempo ofrece dos tipos de Servicios: (i) Qempo Lite:
    el Comprador adquiere directamente el Producto en la Tienda y lo envía a la dirección del Proveedor Logístico, que
    siempre será una empresa de carga internacional, a fin de que este pueda gestionar la importación y entrega del
    mismo al Comprador (en adelante, <strong>“Servicio Qempo Lite”</strong>) y (ii) Qempo Full: Qempo se encarga del
    proceso de compra, recepción, importación y de entrega del Producto a través de sus Proveedores Logísticos (en
    adelante, <strong>“Servicio Qempo Full”</strong>).</p>
  <br/>
  <h3>3.1.1 El Comprador</h3>
  <p>Mediante el Portal, el Comprador informará a la comunidad de Proveedores Logísticos de Qempo sobre su interés en
    adquirir el Producto en la Tienda correspondiente, completando los datos requeridos por Qempo (en adelante,
    el <strong>“Pedido”</strong>), entre ellos, el precio, las dimensiones, el peso del Producto, tipo de Producto,
    entre otros (en adelante, los <strong>“Datos del Pedido”</strong>).</p>
  <br/>
  <p>El Comprador deberá indicar en el Portal el URL que corresponda al Producto. El Comprador se encontrará impedido de
    realizar pedidos no especificados, genéricos, o aquellos que, siendo identificados, no se encuentran acompañados de
    la URL correspondiente.</p>
  <br/>
  <p>Asimismo, el Comprador declara y reconoce que Qempo no asume responsabilidad alguna en caso los Datos del Pedido
    ingresados por el Comprador o por el sistema fueran imprecisos o incorrectos, que puedan implicar costos de
    importación adicionales por parte del Comprador; de ese modo, el Comprador acepta y reconoce que será el único y
    exclusivo responsable por los Datos del Pedido ingresados.</p>
  <h3>3.1.2 Los Proveedores Logísticos</h3>
  <p>Los Proveedores Logísticos podrán ser: (i) viajeros y/o (ii) empresas y/o agencias courier o de carga
    internacional.</p>
  <br/>
  <p>3.1.2.1 Los Viajeros</p>
  <p>Cuando el Proveedor Logístico del “Servicio Qempo Full” sea un Viajero, este se obliga a adquirir el Producto
    mediante el URL provisto por el Comprador por medio del Portal; el Viajero se encontrará impedido de adquirir el
    Producto de un proveedor distinto al seleccionado por el Comprador.</p>
  <br/>
  <p>Por cada Pedido publicado en el Portal, Qempo comunicará al Comprador la Contraprestación a pagar, de acuerdo a los
    Datos del Pedido ingresados por el Usuario.</p>
  <p>El Viajero, al momento de manifestar su disponibilidad para la prestación de los Servicios en relación con un
    Pedido, garantiza que cuenta con un vuelo confirmado para la fecha registrada, debiendo comunicar a Qempo: (i) su
    fecha de viaje desde la ciudad de embarque (en adelante, la <strong>“Ciudad de Embarque”</strong>) y (ii) su fecha
    de arribo a la ciudad donde se encuentre el Comprador (en adelante, la <strong>“Ciudad de Destino”</strong>). En
    caso las fechas de sus vuelos sufrieran cambios o retrasos, el Viajero se obliga a comunicar inmediatamente a Qempo
    de dicho evento al correo electrónico: <a href="mailto:ayuda@qempo.com">ayuda@qempo.com.</a></p>
  <br/>
  <p>El Viajero reconoce que la adquisición, la recepción, el transporte y la entrega del Producto es responsabilidad
    exclusiva del Viajero, eximiendo a Qempo de toda responsabilidad por cualquier error, incumplimiento, daño y/o
    delito que el Viajero, y/o cualquier otro tercero, vinculados a ellos o no, pudieran cometer dentro y/o fuera del
    Portal.</p>
  <br/>
  <p>Un Usuario podrá será considerado Viajero cuando, a través del Portal, publique su próximo viaje e indique su
    disponibilidad para comprar y recibir el Producto en su Ciudad de Embarque, a fin de transportarlo y entregarlo o
    enviarlo en la Ciudad de Destino.</p>
  <br/>
  <p>Qempo pagará la contraprestación que le correspondiera al Viajero luego de recibido el Producto en sus oficinas y
    siempre que el equipo de Qempo verifique el correcto estado del mismo. En caso el Viajero no entregue el Pedido a
    Qempo en el plazo indicado o entregue el Pedido en malas condiciones, Qempo podrá penalizar al Viajero o incluso
    podrá negarse a recibir el Pedido; siendo que en este último escenario, Qempo no se encontrará obligado a efectuar
    el pago de la contraprestación que le hubiera correspondido al Viajero ni reembolsar los gastos incurridos.</p>
  <br/>
  <p>En algunos casos, Qempo realizará la compra de los Productos y coordinará con el Viajero para que los reciba con 
    la finalidad de trasladarlos y entregarlos a un representante de Qempo en Lima. En dichos casos el Viajero será 
    responsable ante cualquier robo, hurto, daño o pérdida de el/los Producto(s) que pueda ocurrir mientras los 
    Productos estén en su poder, y deberá asumir el 100% del valor indicado de cada Producto en un plazo no mayor a 
    cinco (5) días hábiles desde la fecha en que debía hacer entrega de los Productos inicialmente. En dichos casos, 
    Qempo podrá aplicar además una penalidad equivalente al [50% (cincuenta por ciento)] del valor de los Productos y 
    no se encontrará obligado a efectuar el pago de la contraprestación que le hubiera correspondido al Viajero ni a 
    reembolsar los gastos incurridos.</p>
  <br/>
  <p>Qempo pagará al viajero únicamente el importe indicado como comisión. De generarse algún costo o gasto adicional 
    por cualquier motivo, el Viajero estará obligado a asumir el importe total de estos, sin que Qempo tenga la 
    obligación de pagar monto adicional alguno.</p>
  <br/>
  <p>3.1.2.2 Empresas y/o Agencias de Carga Internacional</p>
  <p>Cuando el Proveedor Logístico del “Servicio Qempo Lite” o del “Servicio Qempo Full” sea una empresa que brinde
    servicios de courier o de carga internacional, estos gestionarán la importación del Producto y el envío del mismo a
    la dirección final del Comprador. La Contraprestación y el detalle de los conceptos incluidos en la misma, serán
    comunicados al Comprador antes de realizar su Pedido.</p>
  <br/>
  <p>La Empresa de Carga Internacional reconoce que la recepción, el transporte y la entrega del Producto es
    responsabilidad exclusiva de la Empresa de Carga Internacional, eximiendo a Qempo de toda responsabilidad por
    cualquier error, incumplimiento, demora, daño y/o delito que la Empresa de Carga Internacional, y/o cualquier otro
    tercero, vinculados a ellos o no, pudieran cometer dentro y/o fuera del Portal.</p>
  <br/>
  <p>Qempo pagará la contraprestación que le correspondiera a la Empresa de Carga Internacional luego de recibida la
    conformidad del Comprador en relación con el Producto entregado o de no haber recibido ningún reclamo dentro de los
    tres (3) días calendario siguientes, contados desde la entrega del Producto.</p>
  <br/>
  <h3>3.2 El Producto</h3>
  <p>Qempo podrá poner a disposición del Usuario en el Portal enlaces, banners, y/u otros medios que refieran a los
    Usuarios a sitios web pertenecientes y/o gestionados por terceros (en adelante, los <strong>“Sitios
      Enlazados”</strong>) para la adquisición de los Productos.</p>
  <br/>
  <p>Qempo no garantiza la licitud, fiabilidad, disponibilidad, utilidad, veracidad, exactitud, legalidad, inocuidad,
    idoneidad y/o actualidad de los productos ofrecidos por los Sitios Enlazados, ni por cualquier tercero, incluyendo
    mas no limitándose al Producto. Asimismo, el Comprador declara conocer que Qempo no asume responsabilidad alguna, ni
    garantiza las condiciones, exactitud, características reales, actualidad e idoneidad del Producto que el Comprador
    hubiera adquirido en la Tienda, ya sea a través del “Servicio Qempo Lite” o a través del “Servicio Qempo Full”. En
    ese sentido, el Usuario excluye a Qempo de cualquier responsabilidad por daños y perjuicios de toda naturaleza que
    puedan originarse por la adquisición, posesión, transmisión, el transporte, la distribución, la manipulación, el
    almacenamiento, la puesta a disposición, la remisión, la recepción, la obtención, el acceso y/o la utilización del
    Producto.</p>
  <br/>
  <p>En caso el Producto adquirido por el Comprador, ya sea a través del “Servicio Qempo Lite” o a través del “Servicio
    Qempo Full”, cuente con garantía ofrecida por la Tienda o por el proveedor del Producto, Qempo podrá garantizar la
    devolución del Producto a Estados Unidos, a fin de gestionar la ejecución de la garantía del Producto en la Tienda o
    ante el proveedor correspondiente (en adelante, la <strong>“Garantía”</strong>). Qempo podrá ofrecer dicha Garantía
    de manera gratuita durante el periodo de tiempo que determine a su sola discreción.</p>
  <br/>
  <p>El Comprador declara reconocer que, en caso el Usuario seleccione como Proveedor Logístico a un Viajero para la
    ejecución de los “Servicios Qempo Full”, será responsabilidad exclusiva del Viajero comprar el Producto desde el URL
    provisto por el Comprador para tales efectos, y será responsabilidad exclusiva del Comprador verificar la integridad
    y exactitud del Producto en el momento de su entrega.</p>
  <br/>
  <p>El Comprador se obliga a abstenerse de publicar solicitudes de compra y transporte de artículos ilícitos, que
    infrinjan normas de propiedad intelectual, que requieran permisos y/o permisos especiales para su importación y/o
    transporte nacional e/o internacional como armas, drogas, explosivos, municiones, semillas, plantas, flores,
    animales, órganos, sustancias tóxicas, mercancías peligrosas, artículos cuya posesión implique un riesgo de
    cualquier naturaleza, artículos prohibidos y/o restringidos por la IATA (International Air Transport Association),
    la ICAO (International Civil Aviation Organization), ADR (European Agreement Concerning the International Carriage
    of Dangerous Goods by Road), u otra organización pertinente; y/o en cualquiera de los países de origen, tránsito y/o
    destino del Viajero, del Comprador y/o del Producto. Asimismo, los Proveedores Logísticos se obligan a no adquirir
    ni transportar los artículos aquí referidos por encargo de ningún Comprador.</p>
  <br/>
  <p>En caso el producto sea solicitado con caja, no quiere decir que este no pueda ser abierto. En muchos casos, los 
    productos son abiertos en la inspección aduanera, tanto para viajeros como para envíos por carga comercial. Asimismo, 
    en el caso de viajeros, los sellos del productos pueden sufrir algunas desperfecciones en el traslado del equipaje. 
    Por tal motivo, El Comprador acepta recibir los productos solicitados con o sin sello de seguridad, pues es un 
    detalle que escapa de las manos de la operación logística y el Viajero o Proveedor Logístico no se pueden hacer 
    responsable por el mismo.</p>
  <br/>
  <h3>3.3 El Pedido, la Propuesta de Servicio y la Orden de Compra</h3>
  <p>El Pedido indicará (i) el URL específico del Producto, incluyendo, la descripción del Producto, su color y la
    cantidad de Productos a adquirir, en caso de ser más de uno; (ii) el precio de compra del Producto en la Tienda;
    (iii) la fecha máxima en la cual el Comprador requiera contar con el Producto en la Ciudad de Destino; y (iv) el
    lugar propuesto para la entrega del Producto por parte del Viajero (en adelante, el <strong>“Punto de
      Entrega”</strong>).</p>
  <br/>
  <p>El Pedido indicará (i) el URL específico del Producto -seleccionado por el Comprador-, incluyendo, los Datos del
    Pedido; es decir, la descripción del Producto, su peso, su color y la cantidad de Productos a adquirir, en caso de
    ser más de uno, entre otros; (ii) el precio de compra del Producto en la Tienda; (iii) la fecha aproximada en la
    cual el Comprador contará con el Producto en la Ciudad de Destino, de acuerdo al tipo de Servicio escogido; (iv) la
    dirección en la Ciudad de Embarque a la que el Comprador debe enviar el Producto; y (v) la dirección en la Ciudad de
    Destino en la que se entrega el Producto al Comprador (en adelante, la <strong>“Punto de Entrega”</strong>).</p>
  <br/>
  <p>Una vez que el Comprador haya ingresado el Pedido, el Comprador podrá visualizar a los diferentes tipos de
    Servicios ofrecidos por Qempo (en adelante, la <strong>“Propuesta de Servicio”</strong>); siendo que podrá
    seleccionar a través del Portal la que desee tomar. Al seleccionar una Propuesta de Servicio, Qempo generará un
    documento en el cual se detalle todos los datos respecto al Producto, al Servicio escogido por el Comprador, y demás
    aspectos que Qempo considere relevantes a su sola discreción (en adelante, la <strong>“Orden de Compra”</strong>).
  </p>
  <br/>
  <p>En la Orden de Compra se indicará: (i) el URL específico del Producto -seleccionado por el Comprador-, incluyendo,
    la descripción del Producto, peso, su color y la cantidad de Productos a adquirir, en caso de ser más de uno; (ii)
    el precio de compra del Producto en la Tienda; (iii) la fecha aproximada en la cual el Comprador contará con el
    Producto en la Ciudad de Destino, de acuerdo al tipo de Servicio escogido; (iv) la contraprestación correspondiente
    a los Proveedores Logísticos; (v) la contraprestación correspondiente a Qempo; (vi) el detalle de los trámites de
    importación y/o Aduanas que correspondan al Producto; (vii) el costo de los trámites de importación y/o Aduanas;
    (viii) el Punto de Entrega del Producto al Comprador.</p>
  <br/>
  <p>La Orden de Compra deberá ser aceptada por el Comprador como condición para su vigencia y exigibilidad. La
    aceptación de la Orden de Compra se materializa mediante el pago efectuado por el Comprador del monto indicado en la
    referida Orden de Compra. En caso que el Comprador realice el pago de un monto inferior al expresado en la Orden de
    Compra, la Orden de Compra se entenderá no aceptada y los Proveedores Logísticos no se encontrarán obligados a
    prestar los Servicios. En tal supuesto, Qempo reembolsará el monto pagado por el Comprador. Si el Comprador pagara
    un monto superior al indicado en la Orden de Compra, esta última se entenderá aceptada, y Qempo procederá a
    reembolsar el exceso pagado al Comprador en un plazo de hasta treinta (30) días hábiles.</p>
  <br/>
  <p>De acuerdo a lo señalado, en la cláusula 3.1.1, el Usuario será el único y exclusivo responsable por los Datos del
    Pedido imprecisos o incorrectos ingresados en la Orden de Compra. En dicho escenario, el Usuario declara y reconoce
    que el cálculo de los costos de importación, que forman parte de la Contraprestación, podrán ser inexactos o
    insuficientes. Por su parte, Qempo se obliga a comunicar al Comprador sobre los costos adicionales que se requieran
    cubrir a fin de corregir el Pedido; siendo que el Usuario tendrá un plazo de cuarenta y ocho (48) horas para
    comunicar a Qempo si efectuará el pago de dichos costos adicionales o si desea cancelar el Pedido.</p>
  <br/>
  <p>Para cualquier cancelación de un pedido, bajo cualquier motivo, el comprador no recibirá el reembolso de la 
    comisión del procesador de pagos (pues el procesador no reembolsa dicha comisión), por lo que recibirá un 5% menos 
    al monto pagado inicialmente como reembolso.</p>
  <br/>
  <p>Para el caso del “Servicio Qempo Lite”, el Comprador se obliga a colocar en el Portal el número de <i>tracking
  </i> del Producto a la brevedad posible; siendo que sin dicha información Qempo no podrá brindar los Servicios.</p>
  <br/>
  <p>Para el caso del “Servicio Qempo Full”, en caso que el Producto indicado en el URL provisto por el Comprador ya no
    se encontrara disponible, el Proveedor Logístico lo informará al Comprador, quien tendrá la opción de (i) cancelar
    la Orden de Compra; o (ii) indicar un nuevo URL al Proveedor Logístico.</p>
  <br/>
  <p>Asimismo, para el caso del “Servicio Qempo Full”, el Proveedor Logístico deberá cargar, en su cuenta dentro del
    Portal, una reproducción de la constancia de compra del Producto haciendo uso del URL específico provisto por el
    Comprador. El incumplimiento de esta obligación implicará la pérdida del derecho de recibir el pago del precio del
    Producto.</p>
  <br/>
  <p>Si el Proveedor Logístico se encontrara dispuesto a aceptar el cambio del URL, el Comprador procederá a emitir un
    nuevo Pedido incluyendo el nuevo URL, lo cual generará una segunda Orden de Compra, momento a partir del cual la
    primera Orden de Compra quedará sin efecto. El monto pagado por el Comprador en relación con la primera Orden de
    Compra servirá como crédito para compensar la obligación de pago relacionada con la segunda Orden de Compra. En
    caso que el monto a pagar por la segunda Orden de Compra exceda el monto pagado por el Comprador en relación con la
    primera Orden de Compra, el Comprador deberá pagar a Qempo la diferencia pendiente para efectos de entender aceptada
    la segunda Orden de Compra. Si el monto relacionado con la segunda Orden de Compra fuese menor que la suma pagada
    efectivamente por el Comprador en relación con la primera Orden de Compra, Qempo reembolsará el exceso pagado al
    Comprador en un plazo de hasta treinta (30) días hábiles.</p>
  <br/>
  <p>Cuando el Proveedor Logístico elegido por el Comprador fuera un Viajero y este ya no contara con la posibilidad de
    adquirir, recibir y transportar el Producto indicado en el nuevo URL provisto por el Comprador dentro de su
    itinerario de viaje, deberá informarlo al Comprador a la brevedad posible. De ocurrir el supuesto indicado
    precedentemente, la Orden de Compra quedará cancelada, no estando ni el Comprador ni el Viajero obligados a pagar
    monto alguno por ningún concepto su contraparte, procediendo Qempo a reembolsar el monto pagado por el Comprador en
    un plazo de hasta treinta (30) días hábiles</p>
  <h3>3.4 Sobre la Adquisición, Recepción, Transporte y Entrega o Remisión del Producto</h3>
  <p>Una vez que el Producto sea recibido -o recogido- por el Proveedor Logístico, éste se encargará de su transporte a
    la Ciudad de Destino, así como de todos los trámites de importación de acuerdo a ley y se encargará de su traslado
    hacia el Punto de Entrega.</p>
  <br/>
  <p>El Proveedor Logístico notificará al Comprador, a través del Portal, cuando el Pedido arribe a la Ciudad de
    Destino; así como cuando el Pedido haya sido enviado al Punto de Entrega. El Comprador podrá variar unilateralmente
    el Punto de Entrega mediante el envío de un correo electrónico a <a href="mailto:ayuda@qempo.com">ayuda@qempo.pe</a>,
    desde el correo electrónico utilizado por el Comprador para contratar los Servicios, y siempre que el Pedido no
    hubiera llegado a la Ciudad de Destino; de lo contrario se mantendrá el Punto de Entrega establecido en la Orden de
    Compra.</p>
  <br/>
  <p>El Comprador declara conocer que las fechas comunicadas por Qempo, en la Propuesta de Servicio y la Orden de Compra
    correspondiente, para la entrega del Producto son aproximadas y referenciales. En ese sentido, el Comprador declara
    conocer que Qempo no asume responsabilidad alguna si la fecha de entrega del Producto propuesta se retrasara: (i) en
    caso el Comprador hubiera adquirido un Producto de una Tienda que se encuentra fuera de Estados Unidos, pero que
    realice entregas a dicho país o, en general, por (ii)causas no imputables a Qempo y/o a los Proveedores Logísticos.
    Por su parte, Qempo se obliga a comunicar al Comprador los cambios en la fecha de entrega del Producto y notificar
    la nueva de fecha de entrega aproximada, en el menor plazo posible.</p>
  <br/>
  <p>En caso que el Comprador y el Proveedor Logístico no logren concretar la entrega del Producto en un plazo de diez
    (10) días calendario contados desde el arribo del Proveedor Logístico y/o del Producto, por causa no imputable al
    Comprador, Qempo podrá devolver – a solicitud del Comprador- el importe pagado por el Comprador en relación con la
    Orden de Compra íntegramente. En este supuesto, el Proveedor Logístico tendrá derecho a devolver el Producto a la
    tienda en la cual se adquirió el Producto, o a retenerlo para sí mismo, sin derecho a recibir pago alguno por parte
    de Qempo, ni por parte del Comprador.</p>
  <br/>
  <p>En caso que el Comprador y el Proveedor Logístico no logren concretar la entrega del Producto en un plazo de diez
    (10) días calendario contados desde el arribo del Proveedor Logístico y/o del Producto a la ciudad de residencia del
    Comprador, por causa no imputable al Proveedor Logístico, Qempo pagará el monto de la Orden de Compra al Proveedor
    Logístico ante la presentación de la evidencia del intento de comunicación con el Comprador, y del intento de
    entrega o remisión del Producto en el Punto de Entrega. En tal supuesto, el Comprador no tendrá derecho a reembolso
    alguno, por parte de Qempo, ni por parte del Proveedor Logístico, debiendo recoger el Producto en las oficinas de
    Qempo, ubicadas en la ciudad de Lima, Perú.</p>
  <br/>
  <p>El Comprador, al momento de la entrega del Producto por parte del Proveedor Logístico, deberá revisarlo a fin de
    constatar que sea el Producto solicitado y que este se encuentre en buen estado. De no ser así, deberá dejar
    constancia de dicho hecho, no recibir el Producto, tomar fotografías del Producto haciendo énfasis en los problemas
    suscitados, y remitir las imágenes a Qempo en un plazo máximo de tres (3) días calendario, contados desde la fecha
    de entrega del Producto. Qempo no se responsabiliza por cualquier reclamo que pueda efectuar el Usuario respecto al
    estado del Producto en caso éste hubiera sido recibido sin seguir el procedimiento detallado anteriormente,
    considerando especialmente que Qempo pagará la contraprestación correspondiente al Proveedor Logístico luego de
    recibida la conformidad del Comprador en relación al Producto o de no haber recibido ningún reclamo dentro de los
    tres (3) dìas calendario siguientes, contados desde la entrega del Producto.</p>
  <h3>3.5 Medios de pago</h3>
  <p>Qempo acepta pagos con tarjeta de crédito, tarjeta de débito, PayPal, bitcoins, y efectivo o pago en banco en Perú.
  </p>
  <br/>
  <p>El Comprador afirma y garantiza ser el titular del medio de pago utilizado para la compra del Producto y la
    contratación del Servicio del Viajero.</p>
  <br/>
  <p>Ante la sola recepción de un reclamo por parte de quien afirme que el Usuario haya utilizado un medio de pago ajeno
    sin ser autorizado por su titular y/o de manera fraudulenta, y/o a sola discreción de Qempo, Qempo se reserva el
    derecho de: (i) solicitar la validación de la identidad del Usuario por medios adicionales a la sola creación de la
    cuenta en Qempo; y/o (ii) cancelar cualquier solicitud de compra del Producto y de la contratación de los Servicios.
  </p>
  <br/>
  <p>Frente a cualquier solicitud de reembolso por parte del Comprador, Qempo realizará el reembolso al mismo medio de
    pago utilizado por el Comprador para contratar los Servicios. En caso el pago de la Contraprestación se hubiera
    realizado con una tarjeta de crédito, el Comprador declara y reconoce que el reembolso de la Contraprestación se
    realizará de acuerdo a los procesos y plazos utilizados por el banco emisor de la tarjeta de crédito; en ese
    sentido, el Comprador mantendrá indemne a Qempo de cualquier reclamo por la demora del desembolso del dinero
    reembolsado. Por su parte, Qempo se obliga a enviar, en el menor plazo posible, la constancia de la devolución al
    Comprador ante el banco emisor de la tarjeta de crédito.</p>
  <h2>4. PROPIEDAD INTELECTUAL DE QEMPO</h2>
  <p>Todas las marcas, nombres comerciales y/o signos distintivos de cualquier clase que aparecen en el Portal son
    propiedad de Qempo, según corresponda y/o de sus respectivos titulares, sin que pueda entenderse que el uso y/o
    acceso al Portal y/o al Servicio del Viajero atribuya al Usuario derecho alguno sobre las citadas marcas, nombres
    comerciales y/o signos distintivos. En todos los casos el uso de marcas, nombres comerciales y/o signos distintivos
    de terceros en el Portal son únicamente con fines informativos para los Usuarios del Portal.</p>
  <br/>
  <p>Asimismo, los contenidos y otros elementos protegidos por la regulación aplicable de propiedad intelectual que
    aparecen en el Portal son de titularidad de Qempo y/o de sus respectivos titulares, sin que puedan entenderse
    licenciados y/o cedidos en sus derechos de uso, comercialización, modificación, y/o explotación comercial en
    cualquier forma, a ningún otro Usuario ni a favor de terceros por su simple carga, publicación, y/o comunicación
    pública en el Portal. En virtud de lo establecido en los presentes Términos y Condiciones, ninguno de los derechos
    existentes y/o que puedan existir en el futuro sobre dichos contenidos y/u otros elementos protegidos por la
    regulación aplicable de propiedad intelectual, podrá ser utilizado por el Usuario más allá de lo estrictamente
    necesario para el correcto uso del Portal y del Servicio, y siempre bajo los principios de buena fe y los usos
    honrados de los mismos.</p>
  <br/>
  <p>El Usuario declara reconocer y aceptar que se encuentra expresamente prohibido descargar, copiar, reproducir,
    volver a publicar, enviar por correo ordinario, transmitir, almacenar, vender o distribuir ejemplares de obras y/o
    de otros derechos de propiedad intelectual contenidos en el Portal, salvo que el Usuario cuente con el permiso
    escrito previo de Qempo y/o de sus personas vinculadas y/o del titular del derecho correspondiente.</p>
  <br/>
  <p>Qempo y sus personas vinculadas se encuentran excluidas de cualquier responsabilidad por los daños y perjuicios de
    toda naturaleza que puedan originarse por la violación de signos distintivos o derechos de propiedad intelectual por
    parte de los Usuarios en el Portal y/o por medio del Servicio del Viajero.</p>
  <h2>5. EXCLUSIÓN DE GARANTÍAS Y DE RESPONSABILIDAD</h2>
  <p>El Usuario declara reconocer y aceptar que Qempo no controla a los Usuarios, a los Compradores, a los Proveedores
    Logísticos, los Sitios Enlazados, ni a cualquier otra página web distinta al Portal, y que Qempo no comercializa,
    directamente ni indirectamente, los Productos que sean ofrecidos al Usuario y/o a los Compradores por medio de la
    Internet, ni aquellos que los Compradores pudieran solicitar, que adquieran, reciban o recojan, transporten y/o
    envíen a los Proveedores Logísticos.</p>
  <br/>
  <p>Qempo únicamente pone a disposición de los Usuarios, los Compradores y los Proveedores Logísticos una plataforma
    informática que facilita la comunicación y conexión entre personas que ofertan y demandan servicios de transporte,
    importación y entrega de productos. En consecuencia, Qempo recomienda a los Usuarios, a los Compradores la
    adquisición del Producto en tiendas fiables y a adoptar las máximas precauciones y medidas de seguridad que le
    resulten posibles con la finalidad de salvaguardar su integridad personal y patrimonial, incluyendo mas no
    limitándose a adquirir el Producto únicamente en tiendas fiables, a coordinar la entrega del Producto en lugares
    públicos concurridos, y a brindar los datos personales mínimos indispensables para la prestación de los Servicios.
  </p>
  <br/>
  <p>En ese sentido, Qempo no garantiza las condiciones, exactitud, características reales, actualidad e idoneidad del
    Producto que el Comprador hubiera adquirido en la Tienda, ya sea a través del “Servicio Qempo Full” o a través del
    “Servicio Qempo Lite”. El Usuario excluye a Qempo de cualquier responsabilidad por daños y perjuicios de toda
    naturaleza que puedan originarse en la adquisición, posesión, transmisión, el transporte, la distribución, la
    manipulación, el almacenamiento, la puesta a disposición, la remisión, la recepción, la obtención, el acceso y/o la
    utilización del Producto. Asimismo, el enlace y la remisión a los Sitios Enlazados, bajo ninguna circunstancia,
    deberá considerarse una garantía ofrecida por Qempo, ni en relación con los Sitios Enlazados, ni en relación con los
    productos ofrecidos a través de ellos. La inclusión de los Sitios Enlazados en Qempo es meramente referencial.</p>
  <br/>
  <p>Qempo no será responsable por la licitud, fiabilidad, veracidad, pertinencia, relevancia, inocuidad ni idoneidad
    del Producto adquirido por el Comprador mediante los Servicios, ni de ningún otro producto ofrecido mediante los
    Sitios Enlazados y/o por medio de cualquier otra Tienda en la cual el Comprador decida adquirir el Producto. El
    Usuario declara reconocer y aceptar que será el único y exclusivo responsable de la verificación de la veracidad,
    licitud y fiabilidad de los URL de los Sitios Enlazados y de cualquier otra Tienda en la cual los Usuarios y
    Compradores decidan adquirir los Productos, y renuncia a toda acción legal, judicial y extrajudicial, mediante la
    cual se pretenda atribuir responsabilidad de cualquier naturaleza, y bajo cualquier modalidad, a Qempo.</p>
  <br/>
  <p>Si bien Qempo monitorea constantemente la ausencia de virus y de otros elementos maliciosos en el Portal, el
    Usuario declara reconocer y aceptar que, en el supuesto negado de una intrusión informática, el Usuario podría verse
    afectado por alteraciones en su sistema informático (software y hardware) y/o en los archivos electrónicos y/o
    ficheros almacenados en su sistema informático y/o por la interceptación de información y/o datos personales
    remitidos por los Usuarios, Compradores, Proveedores Logísticos por cualquier medio, incluyendo mas no limitándose
    al Portal. El Usuario se obliga a acceder al Portal siempre y cuando cuente con software antivirus y
    antispyware idóneo y actualizado en cualquier dispositivo por medio del acceda al Portal.</p>
  <br/>
  <p>Usted excluye a Qempo, y a sus personas vinculadas, accionistas, Directores, Gerentes, trabajadores y proveedores,
    de toda responsabilidad relacionada con la comisión de delitos, faltas, daños y/o perjuicios, de cualquier
    naturaleza, que puedan deberse a (i) la presencia de virus y/o a la presencia de otros elementos maliciosos en
    general que puedan producir alteraciones en el sistema informático, documentos electrónicos y/o ficheros de los
    Usuarios; y/o (ii) a cualquier acto realizado mediante dolo y/o negligencia por cualquier Usuario, Comprador,
    Proveedor Logísticos y/o cualquier persona en general, haciendo uso del Portal y/o de la información y/o los datos
    personales ingresados y/o transmitidos entre los Usuarios, Compradores y/o Proveedores Logísticos mediante el
    Portal; y/o (iii) a la transmisión, transporte, distribución, almacenamiento, puesta a disposición, recepción,
    obtención, acceso y/o utilización del Producto.</p>
  <br/>
  <p>El Usuario declara reconocer y aceptar que toda compra por medio de la Internet conlleva un riesgo de intrusión
    informática y de recopilación no autorizada de datos personales y/o sobre los medios de pago utilizados por el
    Usuario en el marco de la prestación del Servicio. Qempo hará sus mejores esfuerzos por mitigar dichos riesgos, pero
    no será responsable por la comisión de interceptaciones e/o intrusiones informáticas en perjuicio de Qempo y/o del
    Usuario.</p>
  <br/>
  <p>Qempo no será responsable por la licitud, fiabilidad, veracidad, pertinencia, relevancia, inocuidad ni idoneidad
    del Producto obtenido por el Comprador mediante el Servicio del Viajero, ni de ningún otro producto ofrecido
    mediante los Sitios Enlazados y/o por medio de cualquier otra tienda a la cual el Comprador y/o el Viajero decidan
    comprar el Producto. </p>
  <h2>6. REQUERIMIENTOS DEL SISTEMA</h2>
  <p>Al utilizar el Portal y/o el Servicio, el Usuario acepta que el hardware, software y acceso a Internet empleados
    por el propio Usuario, juegan un papel crucial en su experiencia de navegación. En ese sentido, el Usuario acepta
    que Qempo no será responsable por ningún desperfecto de hardware, software y/o de acceso a Internet del propio
    Usuario y/o de Qempo, ni por intrusiones informáticas y/o por la inyección de software malicioso, y por tanto exime
    de responsabilidad a Qempo por dichas situaciones.</p>
  <br/>
  <p>El Usuario es el único y completo responsable por los gastos de conexión a Internet, así como por todo el software
    y hardware necesario para ello, su revisión, seguridad, reparación y/o corrección y/o cualquier otro costo y/o gasto
    por la operación, el mantenimiento y la protección de los mismos.</p>
  <br/>
  <p>Qempo se reserva el derecho de comprobar automáticamente la versión del software del computador que utiliza el
    Usuario para acceder al Portal y/o al Servicio, a fin de informar al Usuario en caso requiera instalar y/o
    actualizar determinado programa para acceder y/o mejorar su acceso a los mismos, sin que ello pueda ser considerado
    una obligación de Qempo, bajo ningún supuesto.</p>
  <h2>7. CORTES DEL SERVIDOR</h2>
  <p>El Usuario declara conocer que podrían existir periodos de mantenimiento del servidor que aloja el Portal, así como
    también periodos de mantenimiento del Portal mismo. Qempo hará su mejor esfuerzo para limitar el tiempo de
    inactividad del servidor, y no será responsable por ningún daño sufrido por el Usuario como resultado del
    mantenimiento u otros tiempos de inactividad del Portal, sea cual fuere el motivo, incluso si se encuentra dentro de
    la esfera de control de Qempo. El Usuario acuerda eximir de responsabilidad a Qempo por cualquier daño sufrido,
    incluido el lucro cesante, el daño emergente y/o el daño ulterior que puedan ocasionarse por tales hechos, y
    renuncia anticipadamente a cualquier acción legal administrativa, judicial y extrajudicial al respecto, así como
    también a los plazos para interponerlas.</p>
  <br/>
  <p>Qempo no será responsable por ningún daño o perjuicio sufrido por el Usuario, directa e/o indirectamente, por la
    interrupción del acceso y/o la disponibilidad del Portal y/o del Servicio por cualquier motivo, incluyendo mas no
    limitándose a causas previstas o imprevistas y/o causas dentro y/o fuera del control de Qempo, tales como eventos
    que puedan considerarse caso fortuito y/o fuerza mayor, eventos o fenómenos naturales, falla en las
    telecomunicaciones, cortes de electricidad, ataques informáticos, y demás situaciones posibles, de cualquier
    clase.</p>
  <h2>8. MODIFICACIÓN</h2>
  <p>Qempo se reserva el derecho a eliminar y/o modificar en cualquier momento y sin previo aviso, parte del Portal y/o
    de los Servicios, así como de estos Términos y Condiciones, los cuales el Usuario declara haber leído y aceptado en
    su integridad como condición previa cada vez que acceda al Portal y/o contrate los Servicios.</p>
  <h2>9. DURACIÓN Y TERMINACIÓN</h2>
  <p>El Portal y el Servicio tienen, en principio, una duración indefinida. No obstante ello, Qempo podrá suspender y/o
    dar de baja unilateralmente al Portal y/o a los Servicios, en cualquier momento, sin expresión de causa y sin que
    ello pueda originar contraprestación, penalidad ni indemnización alguna a favor suyo y/o a favor de cualquier
    Usuario y/o Comprador. Cuando ello sea razonablemente posible, Qempo advertirá previamente al Usuario sobre la
    suspensión o baja del Portal y/o del Servicio. Sin perjuicio de lo señalado, en caso un Comprador tuviera un Pedido
    en curso, Qempo se obliga a gestionar la entrega de dicho Pedido.</p>
  <h2>10. LEGISLACIÓN Y JURISDICCIÓN APLICABLE</h2>
  <p>Los presentes Términos y Condiciones se rigen, en todo lo no dispuesto en el presente documento, por las normas
    legales aplicables del Estado de Florida, Estados Unidos de América. Qempo y Usted renuncian expresamente a
    cualquier otro fuero y/o jurisdicción que pudiese corresponderle.</p>
  <h2>11. GENERALIDADES</h2>
  <p>En el caso de que cualquier disposición o disposiciones de los Términos y Condiciones fuesen considerados total o
    parcialmente nulos e/o inaplicables, por cualquier juzgado, tribunal y/u órgano administrativo competente, dicha
    nulidad o inaplicación no afectará a las otras disposiciones no declaradas nulas e/o inaplicables
    específicamente.</p>
  <h2>12. CONTACTO</h2>
  <p>
    Para cualquier contacto con Qempo, el Usuario deberá comunicarse al correo electrónico: <a
    href="mailto:hola@qempo.com">hola@qempo.com</a>.
  </p>
  <br/>
  <section>
    <p>
      Términos y Condiciones versión 1.2 vigentes desde el día
    </p>
    <p>
      25 de Octubre del 2018.
    </p>
    <br/>
    <p>
      <strong>QEMPO.COM</strong> es un portal administrado por <em>QEMPO LLC</em>
    </p>
    <p>
      © QEMPO LLC – Todos los derechos reservados
    </p>
  </section>
</section>;
const tsEN = <section className={style.termsServiceWrap}>
  <h1>
    TERMS AND CONDITIONS FOR USING QEMPO.COM WEBSITE
  </h1>
  <p>This Privacy and Personal Data Protection Policy (hereinafter, the <strong>"Privacy Policy"</strong>) governs the
    personal data submission made by users of the website named Qempo, located at the Uniform
    Resource Locator (hereinafter, the <strong>"URL"</strong>) <a href="http://www.qempo.com"
                                                                  target="_blank">http://www.qempo.com</a> (hereinafter,
    the <strong>"Website"</strong>) which Qempo LLC (hereinafter, <strong>"Qempo"</strong>) makes available to its
    users.
  </p>
  <br/>
  <p>Your sign-up, access and/or use of the contents and/or the services rendered by the Website grant you the user
    condition of the Website (hereinafter, <strong>“User”</strong>, and it includes the
    terms <strong>“User”</strong> and <strong>“You”</strong> in singular and the term <strong>“Users”</strong> in
    plural) and involves that you wholly accept without limitation any and all provisions set forth in
    these Terms and Conditions in the version posted by Qempo when You access to the Website to sign-up, access and/or
    use its contents and/or services.</p><p>Therefore, you represent to acknowledge and accept that you undertake to
  read fully these Terms and Conditions every time where the contents and/or the services of the Website are proposed
  to be accessed and/or used and these Terms and Conditions may be subject to any amendment upon Qempo sole discretion
  without requiring a prior notice upon it is posted in the Website.</p><p>Capitalized terms which are not expressly
  defined in this Privacy Policy shall have the definitions stated under the General Terms and Conditions of the
  Website.</p>
  <br/>
  <p><strong>IF YOU DISAGREE WITH THESE TERMS AND CONDITIONS WHICH ARE MANDATORY AND BINDING FOR YOU, YOU SHALL STOP
    YOUR
    SIGN-IN PROCESS IN THE WEBSITE OR, IF YOU HAVE ALREADY MADE IT, YOU SHALL PREVENT FROM ACCESSING OR USING THEREOF,
    ITS CONTENTS AND/OR ITS SERVICES.</strong></p>
  <h2>1. QEMPO AND THE WEBSITE </h2>
  <p>
    By the Website, Qempo enables interaction of persons willing to purchase
    products marketed in different territories other than cities where they are
    located (hereinafter, <strong>"Purchaser"</strong> or <strong>"Purchasers"</strong>), with
    persons willing to buy, receive, transport or submit them to Purchasers in
    exchange of an economic consideration agreed in advance (hereinafter, <strong>"Traveler"</strong>
    or <strong>"Travelers"</strong>).
  </p>
  <br/>
  <p>
    Users may act as Purchasers and/or Travelers in their account in the
    Website.
  </p>
  <h2>2. SIGN-UP, ACCESS AND USE CONDITIONS OF THE WEBSITE </h2>
  <h3>2.1 Legal Capacity </h3>
  <p>
    The Website use is available for persons with legal capacity to contract.
    To sign up, access and use the Website, User shall have, at least, eighteen
    (18) years old.
  </p>
  <h3>2.2 User Sign-up and Sign-In </h3>
  <p>
    Upon signing up to use the Website, User shall provide Qempo the required
    information and shall choose a password for his account. When the stated
    data are submitted, User shall be addressed to his account within the
    Website.
  </p>
  <br/>
  <p>
    User shall ensure that email address provided shall be valid and shall
    continue as valid during the term of this agreement because all notices
    from Qempo to User out of the Website shall be submitted to email address
    used to sign up to Website.
  </p>
  <br/>
  <p>
    User undertakes to provide correct and complete information when signing up
    in the Website by linking his profile of social network located at the URL
    <a href="http://www.facebook.com"
       target="_blank">http://www.facebook.com</a> (hereinafter, <strong>"Facebook"</strong>) to his Qempo
    account. Furthermore, User expressly authorizes Qempo to post and submit
    messages in his name by Facebook.
  </p>
  <br/>
  <p>
    Personal data obtained by User sign-up shall be included in a database
    owned by Qempo (hereinafter, the <strong>“Database”</strong>). Database shall be governed by
    conditions stated in Privacy and Personal Data Protection Policy.
  </p>
  <br/>
  <p>
    User is liable for keeping confidentiality of his sign-in and password
    data, as well as to prevent from intrusions to systems of computer and/or
    mobile phone devices used to access to the Website and/or Service. For this
    purpose, User shall be deemed sole liable for actions and activities made
    under his sign-in data and/or password used to access and/or use the
    Website. User undertakes to not disclose, whether directly or indirectly,
    his password to third parties under no circumstance and/or reason, as well
    as to prohibit third parties from using his sign-in data and/or password.
  </p>
  <br/>
  <p>
    If User losses his user name and/or password, or if User deems this
    information is endangered in any way, and/or if User suspects that his
    account has been used by non-authorized third parties, User undertakes to
    change his password immediately and to inform Qempo, as soon as possible,
    by sending an email to <a href="mailto:seguridad@qempo.com">seguridad@qempo.com</a>.
  </p>
  <h3>2.3 Information Truthfulness </h3>
  <p>
    All information provided by User to Qempo shall be true. For this purpose,
    User warrants the authenticity of those data submitted to Qempo during his
    sign-up and use of the Website.
  </p>
  <br/>
  <p>
    Qempo does not control, monitor, review nor validate information provided
    by Users during sign-up process in the Website, nor during their stay
    therein, so that it is assumed in good faith that all information provided
    by Users is correct, complete, lawful and convenient to these Terms and
    Conditions, law, morals and good habits.
  </p>
  <br/>
  <p>
    User represents that Qempo shall not be liable for any damage and/or harm
    suffered by User and/or any other person due to misrepresentation, errors,
    defects and/or omissions in the information provided by any of the Users in
    the Website and/or by the use of the Website.
  </p>
  <br/>
  <p>
    Furthermore, Qempo reserves the right to suspend and/or cancel User account
    from the Website, as well as to inform corresponding governmental
    authorities if it is aware that false and/or inaccurate data and/or
    information is included as well as in any alleged case of identity theft in
    the Website.
  </p>
  <h3>2.4 Liability to Use Properly the Website </h3>
  <p>
    Each User may have only one (01) account in the Website and is prohibited
    from creating more accounts with different emails or faking, amending or
    altering his personal data in any way. If Qempo is aware that a User has
    more than one (01) account, Qempo may, upon its sole discretion, (i) keep
    active the first account that User has created and void other accounts,
    without requiring prior notice to User for this purpose; and/or (ii) cancel
    all User accounts including, in both cases, annulment of any order,
    purchase order and/or request generally, upon its sole discretion, and the
    User shall not have right to any payment nor reimbursement.
  </p>
  <br/>
  <p>
    User undertakes to access to the Website and to use the Service in good
    faith, pursuant to law and according to these Terms and Conditions. In this
    regard, User:
  </p>
  <ul>
    <li>
      i) Shall not share, market, exchange, sell, purchase nor distribute sign-in
      data and/or passwords to access and/or use the Webpage and/or the Service.
    </li>
    <li>
      ii) Shall not use any language, which upon Qempo sole discretion, may be
      deemed unlawful, intimidating, excessive, obscene, harmful, slanderous,
      rude, stalking, detestable, racist and/or reprehensible, nor any language
      which may be deemed inappropriate and/or contrary to morals and good habits
      when communicating with Qempo staff, any Traveler and/or any Purchaser in
      the Website.
    </li>
    <li>
      iii) Shall not use the Website and/or the Service offered by Qempo for
      inappropriate and/or unlawful purposes.
    </li>
    <li>
      iv) Shall not provide links to any website containing undue and/or unlawful
      material and/or damaging and/or impairing Qempo computer systems.
    </li>
    <li>
      v) Shall not take undue advantage of any problem and/or failure in
      operation, design and/or efficiency of the Website and/or the Service. User
      shall not inform, directly nor indirectly, this problem and/or failure to
      third parties.
    </li>
    <li>
      vi) Shall not inform any personal data, private, sensitive nor personal
      information from any other User and/or third parties by the Website, in no
      manner and under no circumstances.
    </li>
    <li>
      vii) Shall not use, create nor provide any simulation and/or fraudulent
      copy of the Website and/or Qempo servers and/or related persons.
    </li>
    <li>
      viii) Shall not attempt to interfere, make reverse engineer and/or decode
      any transmission of the servers from Qempo and/or its related persons
      and/or the Webpage.
    </li>
    <li>
      ix) Shall not infringe any industrial and/or intellectual property rights
      from Qempo, its related persons nor third parties. User shall not load,
      transmit and/or inform in the Website, nor by the Website, materials
      protected by intellectual property rights unless User has required
      authorization to make these actions provided by the corresponding owners of
      intellectual property rights.
    </li>
    <li>
      x) Shall not interfere with capacity and freedom from other Users to access
      and/or use the Website and/or the Service.
    </li>
  </ul>
  <p>
    The failure of User to comply with any of the aforementioned listed
    conditions or other conditions contained in these Terms and Conditions
    shall grant Qempo the option to suspend and/or cancel definitely the
    infringing User account, and if Qempo deems convenient, Qempo may prosecute
    the stated facts before the corresponding authorities.
  </p>
  <h3>2.5 Suspension and Cancellation of Users </h3>
  <p>
    Qempo may issue warnings, provisional suspensions, and final cancellations
    to Users at any time, without cause nor reason, which shall not be solely
    conditioned to those Users infringing these Terms and Conditions and/or any
    notices, regulations for use and/or instructions informed by Qempo to
    Users.
  </p>
  <p>
    In this regard, Qempo reserves the criterion related to when, to whom and
    how to impose warnings, penalties and/or disciplinary measures. The
    importance of a breach and the number of events are taken into account in
    decisions, notwithstanding the actions, which Qempo shall decide to
    implement upon its sole discretion.
  </p>
  <h3>2.6 Request data deletion</h3>
  <p>Any user who want to request the elimination of their personal information from the Qempo "Database", must do so 
    formally through a request to hola@qempo.com requesting the elimination of all their data. Qempo will verify the 
    request and comply with all the rules, will proceed to delete your data without any objection.</p>
  <br/>
  <h2>3. PRODUCTS, ORDERS, PURCHASERS AND TRAVELERS </h2>
  <h3>3.1 Purchaser and Traveler </h3>
  <p>
    User shall be deemed Purchaser when, by the Webpage, Purchaser posts his
    purchase intention, including the URL of the specific product (hereinafter,
    the <strong>"Product"</strong>) to be purchased in a certain online store
    (hereinafter, the <strong>"Store"</strong>), so that a Traveler may purchase, receive
    or collect, transport and deliver or submit to Purchaser (hereinafter, the
    <strong>"Traveler Service"</strong>).
  </p>
  <br/>
  <p>
    User shall be deemed Traveler when, by the Webpage, Traveler posts his trip
    and he states his availability to purchase and receive the Product in his
    boarding city (hereinafter, the <strong>"Boarding City"</strong>), to transport and
    deliver or submit it to the city where the Purchaser is located
    (hereinafter, the <strong>"Destination City"</strong>).
  </p>
  <br/>
  <p>
    Purchaser and Traveler acknowledge that, when contacting and agreeing the
    corresponding payment for acquiring, receiving, transporting and delivering
    the Product, both have freely acted by free will and are liable for
    complying with the agreement of both parties, and Qempo is held harmless
    from any error, breach, damage and/or offense which may be committed in/or
    out the Website by Purchaser, Traveler and/or any other third party,
    whether related or not related to them.
  </p>
  <h3>3.1.1 Purchaser </h3>
  <p>
    By the Website, Purchaser shall inform Qempo Traveler group about his
    interest in acquiring the Product by completing the data required by Qempo
    (hereinafter, the <strong>"Order"</strong>).
  </p>
  <br/>
  <p>
    Purchaser shall state the URL related to the Product in the Website.
    Purchaser shall be prevented from making non-specified, general or those
    orders which are identified, but not attached by the corresponding URL.
  </p>
  <h3>3.1.2 Traveler </h3>
  <p>
    Traveler undertakes to acquire the Product by the URL provided by Purchaser
    using the Website. Traveler shall be prevented from acquiring the Product
    from a supplier different from the one selected by Purchaser.
  </p>
  <br/>
  <p>
    Travelers willing to render the Traveler Service for transporting the
    Product stated in the Order may make proposals to Purchaser (hereinafter,
    the <strong>"Traveler Service Proposal”</strong> or the <strong>"Traveler Service Proposals"</strong>) and the
    corresponding
    consideration to
    render this service is determined (hereinafter, the <strong>"Traveler Consideration"</strong>).
  </p>
  <br/>
  <p>
    For each Order posted in the Website, Qempo shall inform to Traveler a
    proposed amount for Traveler Consideration according to the Price, the size
    and/or weight of the Product (hereinafter, the <strong>Proposed Traveler Consideration"</strong>). The
    Proposed Traveler Consideration
    informed by Qempo shall only be for reference purposes and Traveler and
    Purchaser may freely agree on Traveler Consideration according to their
    interests.
  </p>
  <br/>
  <p>
    Traveler, when representing his availability to render the Traveler Service
    related to an Order, shall state to Purchaser: (i) the travel date from the
    Boarding City; (ii) the arrival date to the Destination City; and (iii) the
    proposed Traveler Consideration.
  </p>
  <h3>3.2 The Product </h3>
  <p>
    Qempo may make available to User in the Website links, banners and/or other
    means referring Users to websites owned and/or managed by third parties
    (hereinafter, the <strong>"Linked Sites"</strong>).
  </p>
  <br/>
  <p>
    Purchaser represents that Traveler shall be solely liable for purchasing
    the Product from the URL provided by Purchaser for these purposes, and
    Purchaser shall be solely liable for checking integrity, accuracy and
    suitability of the Product when it is delivered by Traveler.
  </p>
  <br/>
  <p>
    Purchaser undertakes to refrain from posting purchase and transport
    requests of unlawful items, contravening intellectual property rules,
    requiring permits and/or special permits to be imported and or nationally
    and/or internationally transported, weapons, drugs, explosives, munitions,
    products classified as cosmetics, medicines, prepared food, seeds, plants,
    flowers, animals, organs, toxic substances, dangerous goods, items whose
    possession involve any kind of risk, prohibited and/or restricted items by
    IATA (International Air Transport Association), ICAO (International Civil
    Aviation Organization), ADR (European Agreement Concerning the
    International Carriage of Dangerous Goods by Road), or any other relevant
    organization; and/or in any of the origin, transit and/or destination
    country of Traveler and/or the Product. Furthermore, Traveler undertakes to
    not acquire nor transport the aforementioned items as requested by any
    Purchaser.
  </p>
  <br/>
  <p>
    Qempo does not warrant the lawfulness, reliability, availability,
    usefulness, truthfulness, accuracy, legality, harmlessness, suitability
    and/or current accuracy of the products offered by the Linked Sites, nor by
    any third party including without limitation the Product. In this regard,
    User holds Qempo harmless from any kind of damages arising from acquiring,
    possessing, transmitting, transporting, distributing, handling, storing,
    making available, submitting, receiving, obtaining, accessing and/or using
    the Product.
  </p>
  <br/>
  <p>If the product is requested with a box, it does not mean that it cannot be opened. In many cases, the products 
    are opened in the customs inspection, for travelers and for shipments by cargo. Likewise, in the case of travelers, 
    the product stamps may suffer some damage in the transfer of luggage. For this reason, the Buyer agrees to receive 
    the requested products with or without a security seal, as it is a detail that escapes the hands of the logistics 
    operation and the Traveler or Logistics Provider cannot be held responsible for it.</p>
  <h3>
    3.3 The Order, Traveler Service Proposal and Purchase Order
  </h3>
  <p>
    The Order shall state (i) the specific URL of the Product including the
    description of the Product, its color and the amount of the Products to be
    acquired, if it is more than one; (ii) the purchase price of the Product in
    the Store; (iii) the deadline when Purchaser shall have the Product in the
    Destination City; and (iv) the place proposed by Traveler for delivering
    the Product (hereinafter, the <strong>Delivery Point"</strong>).
  </p>
  <br/>
  <p>
    When the Purchaser has received one or more Traveler Service Proposals,
    Purchaser may choose the one he preferred by the Website. When choosing one
    Traveler Service Proposal, Qempo shall issue a document whereby data
    related to the Product, the Traveler Service and other issues deemed
    relevant by Qempo upon its sole discretion shall be detailed (hereinafter,
    the <strong>Purchase Order"</strong>).
  </p>
  <br/>
  <p>
    It shall be stated in the Purchase Order: (i) the specific URL of the
    Product including the description of the Product, its color and the amount
    of the Products to be acquired, if it is more than one; (ii) the purchase
    price of the Product in the Store; (iii) the fee owed to Traveler; (iv) the
    fee owed to Qempo; (v) the details of import and/or customs formalities
    related to the Product (vi) the cost of import and/or customs formalities;
    (vii) the date, time and Delivery Point of the Product to Purchaser.
  </p>
  <br/>
  <p>
    The Purchase Order shall be accepted by Purchaser and Traveler as a
    condition for its effectiveness and enforceability. The acceptance of the
    Purchase Order shall be evidenced by the payment of the amount stated in
    the referred Purchase Order made by Purchaser. If Purchaser pays less than
    the amount stated in the Purchase Order, Purchase Order shall be deemed not
    accepted and Traveler shall not undertake to render the Traveler Service.
    In this case, Qempo shall reimburse the amount paid by Purchaser. If
    Purchaser pays more than the amount stated in the Purchase Order, Purchase
    Order shall be deemed accepted and Qempo shall reimburse the amount paid in
    excess to Purchaser.
  </p>
  <br/>
  <p>
    In case the Product stated in the URL provided by Purchaser is no longer
    available, Traveler shall inform to Purchaser, who shall have the option to
    (i) cancel the Purchase Order; or (ii) state a new URL to Traveler.
  </p>
  <br/>
  <p>
    If Traveler is willing to accept to change the URL, Purchaser shall issue a
    new Order including the new URL, which shall create a second Purchase Order
    and upon this time, the first Purchase Order shall not be effective. The
    amount paid by Purchaser related to the first Purchase Order shall be used
    as credit to offset the payment obligation related to the second Purchase
    Order. If the amount to be paid by the second Purchase Order exceeds the
    amount paid by Purchaser related to the first Purchase Order, Purchaser
    shall pay Qempo the pending balance to deem accepted the second Purchase
    Order. If the amount related to the second Purchase Order is less than the
    amount effectively paid by Purchaser related to the first Purchase Order,
    Qempo shall reimburse the amount paid in excess to Purchaser.
  </p>
  <br/>
  <p>
    If the Traveler may not acquire, receive and transport the Product stated
    in the new URL provided by Purchaser within his travel itinerary, Traveler
    shall inform to Purchaser as soon as possible. If the aforementioned event
    occurs, the Purchase Order shall be cancelled and Purchaser nor Traveler do
    not undertake to pay any amount for any reason to the other party and Qempo
    shall reimburse the amount paid by Purchaser.
  </p>
  <br/>
  <p>
    Traveler shall pay, in his account within the Website, a copy of the
    purchase receipt of the Product by using the specific URL provided by
    Purchaser. The failure to comply with this obligation shall cause the loss
    of the right to receive the payment related to the Product.
  </p>
  <h3>
    3.4 Acquiring, Receiving, Transporting and Delivering or Submitting the
    Product
  </h3>
  <p>
    When the Product is delivered or collected by Traveler, Traveler shall be
    liable for transporting the Product to the Destination City as well as for
    making all import formalities according to law and finally transporting to
    the Delivery Point.
  </p>
  <br/>
  <p>
    Traveler shall send a message to Purchaser for confirming day, hour and
    Delivery Point when Traveler arrives to Destination City. Neither Traveler
    nor Purchaser may unilaterally change the Delivery Point, so that if any of
    them required to make a change, this change shall be accepted and validated
    by the other party. If the other party does not accept this change, the
    Delivery Point stated in the Purchase Order shall be kept.
  </p>
  <br/>
  <p>
    If Purchaser and Traveler may not deliver the Product within a term of
    three (03) calendar days after the Traveler has arrived to the city where
    Purchaser lives, for a cause non- attributable to Purchaser, Qempo shall
    return the total amount paid by Purchaser in connection with the Purchase
    Order. In this case, Traveler shall be entitled to return the Product to
    the store where the Product was acquired, or to keep it for himself,
    without right to receive any payment by Qempo nor Purchaser.
  </p>
  <br/>
  <p>
    If Purchaser and Traveler may not deliver the Product within a term of
    three (03) calendar days after the Traveler has arrived to the city where
    Purchaser lives, for a cause non- attributable to Traveler, Qempo shall pay
    the amount of the Purchase Order to Traveler upon filing evidence of
    communication attempt with Purchaser, and the delivery or submission
    attempt of the Product in the Delivery Point. In this case, Purchaser shall
    not be entitled to any reimbursement by Qempo nor Traveler.
  </p>
  <br/>
  <p>
    When Traveler delivers the Product, Purchaser shall review the Product to
    evidence that it is in good condition. Otherwise, Purchaser shall evidence
    this fact, shall not receive the Product, shall take photos of the Product
    emphasizing in the problems occurred and shall immediately submit the
    images to Qempo. Qempo shall not be liable for any claim made by User
    related to the condition of the Product if the Product has been received
    without taking into account the aforementioned procedure detailed and it
    shall be specially taken into account that Qempo shall pay the Traveler
    Consideration to Traveler after receiving the compliance of Purchaser in
    connection with the Product.
  </p>
  <h3>3.5 Means of payment </h3>
  <p>
    Purchaser represents and warrants to be the holder of the means of payment
    used for acquiring the Product and hiring the Traveler Service.
  </p>
  <br/>
  <p>
    Upon the sole delivery of a claim by person asserting that User has used
    other means of payment without authorization of the holder and/or with
    fraud, and/or upon sole discretion of Qempo, Qempo reserves the right to:
    (i) request User identity validity by additional means upon the sole
    account creation in Qempo; and/or (ii) cancel any purchase request of the
    Product and hiring request of the Service.
  </p>
  <h2>4. QEMPO INTELLECTUAL PROPERTY </h2>
  <p>
    Any kind of marks, trade names and/or distinctive signs on the Website are
    owned by Qempo, as the case may be and/or by their corresponding holders,
    and it may not be deemed that the use and/or the access to the Website
    and/or the Traveler Service shall grant the User any right on the
    aforementioned marks, trade names and/or distinctive signs. In every cases,
    the use of marks, trade names and/or distinctive signs from third parties
    in the Website is only for information purposes for Users of the Website.
  </p>
  <br/>
  <p>
    Furthermore, the contents and other elements protected by applicable law of
    intellectual property in the Website are owned by Qempo and/or its
    corresponding holders, and they may not be deemed licensed and/or assigned
    in their rights of use, marketing, amendment and/or business development in
    any manner, to other Users nor in favor of third parties by its loading,
    posting and/o public communication in the Website. Pursuant to the
    provisions set forth in these Terms and Conditions, none of the existing
    rights and/or rights which may arise in the future on these contents and/or
    other elements protected by applicable law of intellectual property, may be
    used by User exceeding which is strictly necessary to use correctly the
    Website and the Service, and always under good faith principles and good
    use thereof.
  </p>
  <br/>
  <p>
    User represents to acknowledge and accept that is expressly prohibited from
    unloading, copying, reproducing, reposting, sending by regular mail,
    transmitting, storing, selling or distributing copies of Works and/or other
    intellectual property rights contained in the Website, unless User has a
    prior written consent from Qempo and/or its related persons and/or the
    holder of the corresponding right.
  </p>
  <br/>
  <p>
    Qempo and its related persons are excluded from any liability for any kind
    of damages arising from infringing distinctive signs or intellectual
    property rights by Users in the Website and/or by the Traveler Service.
  </p>
  <h2>5. EXCLUSION OF WARRANTIES AND LIABILITY </h2>
  <p>
    User represents to acknowledge and accept that Qempo does not control the
    Users, Purchasers, Travelers, the Linked Websites, nor any other webpage
    different from the Website, and that Qempo does not market, directly nor
    indirectly, the Products offered to User and/or Purchasers by internet, nor
    those Products which Travelers may be requested to acquire, receive or
    collect, transport and deliver by Purchasers.
  </p>
  <br/>
  <p>
    Qempo makes only available to the Users, Purchasers and Travelers a
    computer platform enabling communication between persons offering and
    requesting the Service of Travelers. Therefore, Qempo recommends Users,
    Purchasers and Travelers to acquire the Product in reliable stores and
    adopt utmost care and safety measures which may be possible to safeguard
    their personal and property integrity including without limitation to
    purchase only the Product in reliable stores, coordinate the delivery of
    the Product in busy public places and provide minimum personal data
    required to render the Traveler Service.
  </p>
  <br/>
  <p>
    Although Qempo regularly monitors the absence of virus and other malware
    elements in the Website, User represents to acknowledge and accept that,
    even if a computer intrusion occurs, User may be affected by alterations in
    his computer system (software and hardware) and/or in his electronic files
    and/or files stored in his computer system and/or by intercepting
    information and/or personal data submitted by the Users, Purchasers,
    Travelers by any means, including but not limited to the Website. User
    undertakes to access to the Website provided that User shall have suitable
    and updated antivirus software and antispyware in any device which is used
    to access to the Website.
  </p>
  <br/>
  <p>
    You hold Qempo and its related persons, shareholders, Directors, Managers,
    employees and suppliers harmless from committing offenses, misdemeanors,
    damages and/or harms of any nature, which may be caused by (i) the
    existence of virus and/or any other malware element in general which may
    cause alterations in computer system, electronic documents and/or files
    from the Users; and/or (ii) any act made by willful misconduct and/or
    negligence by any User, Purchaser, Traveler, and/or any other person in
    general by using the Website and/or the information and/or the personal
    data entered and/or transmitted among the Users, Purchasers and/or
    Travelers by the Website; and/or (iii) transmission, transport,
    distribution, storage, making available, receipt, obtaining, access and/or
    use of the Product.
  </p>
  <br/>
  <p>
    Link and submission to the Linked Sites, under no circumstance, shall be
    deemed as a warranty offered by Qempo, nor in connection with the Linked
    Sites, nor in connection with the products offered by the Linked Sites. To
    include the Linked Services in Qempo is only for reference purposes. You
    represent to acknowledge and accept that you shall be solely liable for
    checking truthfulness, lawfulness and reliability of the URL of the Linked
    Sites and of any store whereby the Users, Purchasers and Travelers decide
    to purchase the Products and you waive to any legal, court or out-of-court
    action whereby you attempt to attribute any kind of liability under any
    form to Qempo.
  </p>
  <br/>
  <p>
    User represents to acknowledge and accept that any purchase made by
    Internet has a risk of computer intrusion and non-authorized collection of
    personal data and/or means of payment used by User within rendering the
    Service. Qempo shall make reasonable efforts to reduce these risks, but
    Qempo shall not be liable for committing interceptions and/or computer
    intrusions to the detriment of Qempo and/or User.
  </p>
  <br/>
  <p>
    Qempo shall not be liable for lawfulness, reliability, truthfulness,
    appropriateness, importance, harmlessness nor suitability of the Product
    obtained by the Purchaser by the Traveler Service, nor any other product
    offered by Linked Sites and/or by any other store whereby Purchaser and/or
    Traveler may decide to purchase the Product.
  </p>
  <h2>6. SYSTEM REQUIREMENTS </h2>
  <p>
    When using the Website and/or the Service, User accepts that hardware,
    software and access to Internet used by the User play an important role in
    his browsing experience. In this regard, User accept that Qempo shall not
    be liable for any hardware, software and/or access to Internet defect by
    the User and/or by Qempo, nor for any computer intrusion and/or by
    providing malware, and, therefore, User holds Qempo harmless from these
    situations.
  </p>
  <br/>
  <p>
    User is sole and exclusively liable for expenses related to Internet
    connection, as well as for all software and hardware required for this
    purpose, its review, safety, repair and/or correction and/or any other cost
    and/or expense for operating, maintaining and protecting thereof.
  </p>
  <br/>
  <p>
    Qempo reserves the right to verify the computer software version used by
    User to access the Website and/or the Service to inform User if it is
    required to install and/or to update certain program to access and/or
    improve his access thereto, but this may not be deemed a Qempo obligation
    under no case.
  </p>
  <h2>7. SERVER CUTOFFS</h2>
  <p>
    User represents to know that there may be maintenance periods for server of
    the Website, as well as maintenance periods for the Website itself. Qempo
    shall make his best efforts to limit the server idle period and shall not
    be liable for any damage suffered by User as consequence of maintenance or
    other idle periods of the Website, regardless the cause, even if it is
    under the control of Qempo. User agrees to hold Qempo harmless from any
    damage suffered including loss of profit, consequential damage and/or
    further damage which may be caused by those facts, and waives in advance to
    any legal, administrative, court and out-of-court action related thereto,
    as well as to the terms to file them.
  </p>
  <br/>
  <p>
    Qempo shall not be liable for any damage or harm suffered by User, whether
    directly and/or indirectly, by interrupting the access and/or availability
    of the Website and/or the Service for any reason, including without
    limitation foreseen or unforeseen causes and/or causes controlled and/or
    non-controlled by Qempo, such as act of God and/or force majeure, events or
    natural disasters, failure in telecommunications, power failures, computer
    attacks and any other kind of possible situations.
  </p>
  <h2>8. AMENDMENT</h2>
  <p>
    Qempo reserves the right to remove and/or amend at any time without further
    notice, part of the Website and/or the Service, as well as part of these
    Terms and Conditions, which User represents to have wholly read and
    accepted as prior condition when he accesses to the Website and/or the
    rendering of Traveler Service.
  </p>
  <h2>9. TERM AND TERMINATION</h2>
  <p>
    At the beginning, the Website and the Service have an indefinite term.
    Notwithstanding the foregoing, Qempo may suspend and/or cancel unilaterally
    the Website and/or the Service, at any time, without cause and this may not
    cause any consideration, penalty nor compensation to be paid in favor of
    Qempo and/or of any User, Purchaser and/or Traveler. When it is reasonably
    possible, Qempo shall warn User in advance on the Website and/or the
    Service suspension or cancellation.
  </p>
  <h2>10. APPLICABLE LAW AND JURISDICTION </h2>
  <p>
    For every matter, which is not set forth herein, these Terms and Conditions
    are governed by applicable legal rules of the State of Florida, United
    States of America. Qempo and You expressly waive to any other venue and/or
    jurisdiction, which may be competent.
  </p>
  <h2>11. GENERAL PROVISIONS</h2>
  <p>
    If any provision or provisions of the Terms and Conditions are held wholly
    or in part null and/or unenforceable by any competent court, tribunal
    and/or administrative body, this nullity or unenforceability shall not
    affect other provisions not deemed specifically null and/or unenforceable.
  </p>
  <h2>12. CONTACT</h2>
  <p>
    To contact Qempo, User shall send an email to <a href="mailto:hola@qempo.com">hola@qempo.com</a>.
  </p>
  <br/>
  <section>
    <p>
      Version 1.2 of Privacy and Personal Data Protection Policy in force as of
    </p>
    <p>
      October 25, 2018.
    </p>
    <br/>
    <p>
      <strong>QEMPO.COM</strong> is a website managed by <em>QEMPO LLC</em>
    </p>
    <p>
      © QEMPO LLC –All rights reserved
    </p>
  </section>
</section>;

const TermsService = (props: Props, context: Context) => {
  const { locale } = props;
  let content = tsES;
  // T&C NEEDS TRANSLATION
  // if (props.locale.locale === 'en') {
  //   content = tsEN;
  // }
  const title = context.intl.formatMessage((messages[locale.domainCountry] || messages.default).title);
  const titleOg = context.intl.formatMessage((messages[locale.domainCountry] || messages.default).titleOg);
  const description = context.intl.formatMessage((messages[locale.domainCountry] || messages.default).description);
  const descriptionOg = context.intl.formatMessage((messages[locale.domainCountry] || messages.default).descriptionOg);
  const baseUrl = environment.baseUrl[locale.domainCountry || 'US'];
  return <Fragment>
    <Helmet meta={[
      { name: 'description', content: description },
      { property: 'og:image', content: `${baseUrl}${coverImg}` },
      { property: 'og:title', content: titleOg },
      { property: 'og:description', content: descriptionOg },
      { name: 'twitter:title', content: title },
      { name: 'twitter:twitter:image', content: `${baseUrl}${coverImg}` },
      { name: 'twitter:twitter:description', content: description },
    ]}>
      <title>{title}</title>
    </Helmet>
    {content}
  </Fragment>
};

TermsService.propTypes = {
  locale: PropTypes.object,
};
TermsService.contextTypes = {
  intl: intlShape,
};

export default TermsService;



// WEBPACK FOOTER //
// ./src/modules/terms-service/components/TermsService.jsx